import "./DSAPlaylists.scss";
import {Spinner} from "../account/misc/Spinner";
import React, {useCallback, useContext, useEffect, useState} from "react";
import {Button, Input, message} from "antd";
import {DsaPlaylistDto, DsaPlaylistEntryDto, DsaScreenDto} from "../../generated/dashboard-client";
import DSAApi from "../../api/DSAApi";
import {handleServerError} from "../../util/ErrorHandler";
import {CreatePlaylistModal} from "../dsa/CreatePlaylistModal";
import PlaylistIcn from "../../img/PlaylistIcn.png";
import {SearchOutlined} from "@ant-design/icons";
import {AppContext} from "../../AppContext";
import { DeletePlaylistModal } from "../dsa/DeletePlaylistModal";
import PlaylistCard from "../dsa/PlaylistCard";
import {locationsState, mobileState} from "../../state/atoms";
import { useRecoilState, useRecoilValue } from "recoil";
import PreviewModal from "../dsa/PreviewModal";
import { DisplayUploadDto } from "../../domain/DisplayUploadDto";
import QRDisplayUploadsApi from "../../api/QRDisplayUploadsApi";
import DsaTagFilterPills from "../dsa/DsaTagFilterPills";
import { DsaTagItemType, useDsaTags, useDsaTagsLoader } from "../../hooks/useDsaTags";

interface DSAPlaylistsProps {
  screens: DsaScreenDto[];
}

export default function DSAPlaylists({screens}: DSAPlaylistsProps) {
  const [locations] = useRecoilState(locationsState);
  const {location} = useContext(AppContext);
  const mobile = useRecoilValue(mobileState);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [playlists, setPlaylists] = useState([] as DsaPlaylistDto[]);
  const [isCreateModal, setCreateModal] = useState(false);
  const [isDeleteModal, setDeleteModal] = useState(false);
  const [selected, setSelected] = useState({} as DsaPlaylistDto | null);

  const [isPreviewModal, setPreviewModal] = useState(false);
  const [previewEntries, setPreviewEntries] = useState([] as DsaPlaylistEntryDto[]);

  const [pushLoading, setPushLoading] = useState(false);
  const [uploads, setUploads] = useState([] as DisplayUploadDto[]);
  const [selectedTagIds, setSelectedTagIds] = useState([] as number[]);
  const { matchesTagFilter } = useDsaTags();
  useDsaTagsLoader();

  const fetchPlaylists = useCallback(() => {
    setLoading(true);
    DSAApi.getPlaylists().then(r => {
      let toBeScreens = r.data.filter(pl => locations.map(loc => loc.internalId).includes(pl.locationId));
      toBeScreens.sort((a, b) => {
        return Date.parse(b.updatedDate!) - Date.parse(a.updatedDate!);
      });
      setLoading(false);
      setPlaylists(toBeScreens);

      const plToEdit = sessionStorage.getItem('edit-playlist');
      if (plToEdit) {
        setSelected(toBeScreens.find(it => it.id === plToEdit)!);
        setCreateModal(true);
        sessionStorage.removeItem('edit-playlist');
      }
    }).catch(e => {
      setLoading(false);
      handleServerError(e, "Failed to fetch devices.");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUploads = useCallback(() => {
    QRDisplayUploadsApi.getDisplayUploadsByLocationIds(locations.map(l => l.internalId!)).then(resp => {
      setUploads(resp.data);
    })
  }, [locations]);

  useEffect(fetchPlaylists, [fetchPlaylists]);
  useEffect(fetchUploads, [fetchUploads]);

  function showPlaylistBySearch(search: string, playlist: DsaPlaylistDto) {
    return playlist.name!.toLowerCase().includes(search.toLowerCase());
  }

  function isPlaylistShown(playlist: DsaPlaylistDto) {
    return !!playlist.entries?.length
      && showPlaylistBySearch(search, playlist)
      && matchesTagFilter(DsaTagItemType.Playlist, playlist.id!, selectedTagIds);
  }

  const shownPlaylists = playlists.filter(isPlaylistShown);

  function createNewPlaylist() {
    setSelected(null);
    setCreateModal(true);
  }

  function onCreateModalClose(fetch: boolean) {
    setCreateModal(false);
    setSelected({});
    if (fetch) {
      setTimeout(() => {
        fetchPlaylists();
      }, 100);
    }
  }

  function onDeleteModalClose(fetch: boolean) {
    setDeleteModal(false);
    setSelected({});
    if (fetch) {
      setTimeout(() => {
        fetchPlaylists();
      }, 100);
    }
  }

  function pushPlaylistToScreens(playlist: DsaPlaylistDto, screens: DsaScreenDto[], callback?: () => void) {
    setPushLoading(true);
    screens.forEach(sc => {
      try {
        DSAApi.setScreenContent(sc.serial!, {
          type: "PLAYLIST",
          sourceId: playlist.id,
          playable: true
        }).then(() => {
          message.success(`Your playlist has been successfully pushed to your ${screens.length === 1 ? 'screen' : 'screens'}`);
          setPushLoading(false);
          if (callback) {
            callback();
          }
        })
      } catch (e) {
        handleServerError(e);
        setPushLoading(false);
      }
    })
  }

  return <div>
    {loading && <div className={'dsa'}>
      <div className={'spinner'}><Spinner/></div>
    </div>}

    {!loading && <section className={"dsap"}>
      <header>
        {mobile && <Input type="search" prefix={<SearchOutlined/>} placeholder="Search Playlists" value={search}
          onChange={ev => setSearch(ev.target.value)}/>}
        <h3>Playlists</h3>
        {playlists.length > 0 &&
        <Button className={"grey"} onClick={() => createNewPlaylist()}>+ New Playlist</Button>}

        {!mobile && <Input type="search" prefix={<SearchOutlined/>} placeholder="Search Playlists" value={search}
          onChange={ev => setSearch(ev.target.value)}/>}
      </header>

      {playlists.length > 0 && <DsaTagFilterPills selectedTagIds={selectedTagIds} onChange={setSelectedTagIds} />}

      {playlists.length === 0 && <div className={"no-data"}>
        <img src={PlaylistIcn} alt="Icon" />
        <p>
          You don't have any playlists yet.
        </p>
        <Button type={"primary"} size={"large"} onClick={() => setCreateModal(true)}>Create a Playlist</Button>
      </div>}

      {(search || selectedTagIds.length > 0) && playlists.length > 0 && shownPlaylists.length === 0 && <div className="no-matches">
        No matches found.        
      </div>}

      {playlists.length > 0 && <ul className="playlists">
        {shownPlaylists
          .map(it => <PlaylistCard 
            key={it.id}
            it={it} 
            setCreateModal={setCreateModal} 
            setDeleteModal={setDeleteModal} 
            setSelected={setSelected}
            playlists={playlists}
            setPlaylists={setPlaylists}
            locationId={location.internalId ?? 0}
            setPreviewEntries={setPreviewEntries}
            setPreviewModal={setPreviewModal}
            pushPlaylistToScreens={pushPlaylistToScreens}
            pushLoading={pushLoading}
            screens={screens}
            uploads={uploads}
          />)}
      </ul>}
    </section>}

    <CreatePlaylistModal
      isVisible={isCreateModal}
      onClose={onCreateModalClose}
      locationId={location.internalId ?? 0}
      currentPlaylist={selected}
      setCurrentPlaylist={setSelected}
      setPreviewEntries={setPreviewEntries}
      setPreviewModal={setPreviewModal}
      pushPlaylistToScreens={pushPlaylistToScreens}
      pushLoading={pushLoading}
      screens={screens}
    />

    <DeletePlaylistModal
      playlist={selected}
      isVisible={isDeleteModal}
      onClose={onDeleteModalClose}
    />

    <PreviewModal
      previewType="playlist"
      isVisible={isPreviewModal}
      onClose={() => setPreviewModal(false)}
      previewEntries={previewEntries}
      uploads={uploads}
    />
  </div>
}
